import React from 'react';
import { Document, Page, Text, View, Image } from '@react-pdf/renderer';
import { instagramUrl, websiteUrl } from './receiptLinks';
import { getStyles, getDateParts, money, HandleLine } from './receiptPdfShared';

const points = (value) => `${parseFloat(value || 0).toFixed(2)} pts`;

// Return / credit note column widths (Sr, Item, Reason, Qty, Rate, Refund). Hiding
// Rate gives its width back to Item, like the thermal return receipt.
const COLS = { sr: '7%', item: '30%', reason: '20%', qty: '8%', rate: '17%', refund: '18%' };

const RIGHT = { textAlign: 'right' };
const BOLD = { fontWeight: 700 };

const Row = ({ styles, label, value, style }) => (
  <View style={styles.row}>
    <Text style={[styles.rowLabel, style]}>{label}</Text>
    <Text style={[styles.rowValue, style]}>{value}</Text>
  </View>
);

/* The credit note / return receipt as a PDF -- the same content and order as
   ThermalReturnReceipt80mm (what the POS prints), built the way ThermalReceiptPdf
   builds the bill, so it can be sent on WhatsApp like one. */
const ThermalReturnReceiptPdf = ({ receipt }) => {
  const { date, time } = getDateParts(receipt?.completedAt);
  const styles = getStyles(receipt?.receiptFontSize);
  const showRate = receipt?.showRateColumn !== false;
  const decimals = receipt?.itemAmountDecimalPlaces || 2;
  const rateLabel = receipt?.rateColumnLabel || 'Rate';
  const itemWidth = `${parseInt(COLS.item, 10) + (showRate ? 0 : parseInt(COLS.rate, 10))}%`;
  const col = (name, extra) => ({ width: name === 'item' ? itemWidth : COLS[name], paddingRight: 3, ...extra });
  const settlementLines = receipt.settlementLines || [];
  const clawback = parseFloat(receipt.promotionClawbackTotal || 0) > 0;

  return (
    <Document title={`Credit Note ${receipt?.creditNoteNumber || receipt?.returnNumber || ''}`}>
      <Page size={[226.77, 900]} style={styles.page}>
        <View style={styles.center}>
          {receipt.logoUrl ? <Image src={receipt.logoUrl} style={styles.logo} /> : null}
          <Text style={styles.storeName}>{receipt.storeName}</Text>
          {receipt.organizationName ? <Text style={styles.orgName}>{receipt.organizationName}</Text> : null}
          {receipt.storePhone ? <Text style={styles.small}>Mobile: {receipt.storePhone}</Text> : null}
          {receipt.storeEmail ? <Text style={styles.small}>Email: {receipt.storeEmail}</Text> : null}
          {receipt.gstin ? <Text style={styles.small}>GST NO.: {receipt.gstin}</Text> : null}
        </View>

        <View style={styles.divider} />
        <Text style={styles.title}>{receipt.invoiceTitle}</Text>
        <View style={styles.divider} />

        <View>
          <Row styles={styles} label="Return No" value={receipt.returnNumber} />
          {receipt.creditNoteNumber ? <Row styles={styles} label="Credit Note" value={receipt.creditNoteNumber} /> : null}
          <Row styles={styles} label="Date" value={date} />
          <Row styles={styles} label="Time" value={time} />
          <Row styles={styles} label="Original Bill No" value={receipt.orderNumber} />
          <Row styles={styles} label="Refund Method" value={receipt.refundMethod} />
        </View>

        {(receipt.customerName || receipt.customerMobile) ? (
          <>
            <View style={styles.divider} />
            {receipt.customerName ? <Text>Customer: {receipt.customerName}</Text> : null}
            {receipt.customerMobile ? <Text>Mobile: {receipt.customerMobile}</Text> : null}
            {receipt.customerGstin ? <Text>GSTIN: {receipt.customerGstin}</Text> : null}
          </>
        ) : null}

        <View style={styles.divider} />
        <View style={styles.tableHeader}>
          <Text style={col('sr')}>Sr</Text>
          <Text style={col('item')}>Item</Text>
          <Text style={col('reason')}>Reason</Text>
          <Text style={col('qty')}>Qty</Text>
          {showRate ? <Text style={col('rate', RIGHT)}>{rateLabel}</Text> : null}
          <Text style={col('refund', RIGHT)}>Refund</Text>
        </View>
        {receipt.items.map((item) => (
          <View key={`${item.index}-${item.name}`} style={styles.tableRow}>
            <Text style={col('sr')}>{item.index}</Text>
            <View style={col('item')}>
              <Text>{item.name}</Text>
              {item.hsn ? <Text style={styles.subtle}>HSN: {item.hsn}</Text> : null}
            </View>
            <Text style={col('reason', styles.subtle)}>{item.reason || '-'}</Text>
            <Text style={col('qty')}>{item.quantity}</Text>
            {showRate ? <Text style={col('rate', RIGHT)}>{parseFloat(item.rate || 0).toFixed(decimals)}</Text> : null}
            <Text style={col('refund', RIGHT)}>{parseFloat(item.amount || 0).toFixed(decimals)}</Text>
          </View>
        ))}

        <View style={styles.divider} />
        <Row styles={styles} label="Total Refund" value={money(receipt.refundAmount)} style={BOLD} />
        {clawback ? (
          <>
            <Row
              styles={styles}
              label={`Promo Reversed${receipt.promotionClawbackName ? ` (${receipt.promotionClawbackName})` : ''}`}
              value={`-${money(receipt.promotionClawbackTotal)}`}
            />
            {receipt.debitNoteNumber ? <Row styles={styles} label="Debit Note" value={receipt.debitNoteNumber} /> : null}
            <Row styles={styles} label="Net Refund" value={money(receipt.monetaryRefundAmount)} style={BOLD} />
          </>
        ) : null}
        {settlementLines.length > 0 ? (
          <>
            <View style={styles.divider} />
            <Text style={styles.sectionTitle}>Refund Settlement</Text>
            {settlementLines.map((line, i) => (
              <Row
                key={`${line.tenderType}-${i}`}
                styles={styles}
                label={line.tenderType === 'loyalty_points' && line.points != null ? `${line.label} (${points(line.points)})` : line.label}
                value={money(line.amount)}
              />
            ))}
            {receipt.storeCreditBalanceAfter != null ? (
              <Row styles={styles} label="Store Credit Balance" value={money(receipt.storeCreditBalanceAfter)} style={BOLD} />
            ) : null}
            {receipt.loyaltyPointsBalanceAfter != null ? (
              <Row styles={styles} label="Points Balance" value={points(receipt.loyaltyPointsBalanceAfter)} style={BOLD} />
            ) : null}
          </>
        ) : parseFloat(receipt.loyaltyPointsRefunded || 0) > 0 ? (
          <>
            <Row styles={styles} label={`${receipt.refundMethod || 'Amount'} Refunded`} value={money(receipt.monetaryRefundAmount)} />
            <Row styles={styles} label="Points Returned" value={points(receipt.loyaltyPointsRefunded)} />
          </>
        ) : null}

        {receipt.gstSummary.length > 0 ? (
          <>
            <View style={styles.divider} />
            <Text style={styles.sectionTitle}>GST Summary</Text>
            <View style={styles.gstHeader}>
              <Text style={styles.gstColTax}>Tax</Text>
              <Text style={styles.gstCol}>Taxable</Text>
              <Text style={styles.gstCol}>CGST</Text>
              <Text style={styles.gstCol}>SGST</Text>
              <Text style={styles.gstCol}>IGST</Text>
            </View>
            {receipt.gstSummary.map((row) => (
              <View key={`${row.label}-${row.taxableAmount}`} style={styles.gstRow}>
                <Text style={styles.gstColTax}>{row.label}</Text>
                <Text style={styles.gstCol}>{parseFloat(row.taxableAmount || 0).toFixed(2)}</Text>
                <Text style={styles.gstCol}>{parseFloat(row.cgstAmount || 0).toFixed(2)}</Text>
                <Text style={styles.gstCol}>{parseFloat(row.sgstAmount || 0).toFixed(2)}</Text>
                <Text style={styles.gstCol}>{parseFloat(row.igstAmount || 0).toFixed(2)}</Text>
              </View>
            ))}
          </>
        ) : null}

        {receipt.notes ? (
          <>
            <View style={styles.divider} />
            <Text style={styles.sectionTitle}>Notes</Text>
            <Text style={styles.small}>{receipt.notes}</Text>
          </>
        ) : null}

        <View style={styles.divider} />
        <Text style={styles.sectionTitle}>Terms & Conditions</Text>
        <View style={styles.terms}>
          {(receipt.terms || []).map((term) => (
            <Text key={term} style={styles.term}>• {term}</Text>
          ))}
        </View>
        {receipt.declarationText ? (
          <>
            <View style={styles.divider} />
            <Text style={styles.sectionTitle}>Declarations</Text>
            <Text style={styles.declaration}>{receipt.declarationText}</Text>
          </>
        ) : null}

        {receipt.footerMessage ? <Text style={styles.thanks}>{receipt.footerMessage}</Text> : null}
        {receipt.websiteHandle ? <HandleLine styles={styles} text={receipt.websiteHandle} url={websiteUrl(receipt.websiteHandle)} /> : null}
        {receipt.instagramHandle ? <HandleLine styles={styles} text={receipt.instagramHandle} url={instagramUrl(receipt.instagramHandle)} /> : null}
      </Page>
    </Document>
  );
};

export default ThermalReturnReceiptPdf;
